"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useWindowStore } from "../windows/windowStore";
import RotateHandle from "../ui/RotateHandle";

type Orientation = "horizontal" | "vertical";

type RulerProps = {
  orientation?: Orientation;
  lengthPx?: number; // visual length in pixels
  windowId?: string;
};

// Generates tick marks for both cm and inches scales
function useTicks(lengthPx: number) {
  // CSS px is 1/96 inch. For typical displays, using 96 keeps geometry consistent.
  const pxPerInch = 96; // CSS pixel baseline
  const pxPerCm = pxPerInch / 2.54;

  // Create ticks up to given length
  const inchTicks = useMemo(() => {
    type InchKind = "major" | "half" | "quarter" | "eighth" | "sixteenth";
    const count = Math.floor(lengthPx / pxPerInch) + 1;
    const ticks: { x: number; label?: string; kind: InchKind }[] = [];
    for (let i = 0; i <= count * 16; i++) {
      const sixteenth = i / 16;
      const x = Math.round(sixteenth * pxPerInch);
      const isWhole = i % 16 === 0;
      const isHalf = !isWhole && i % 8 === 0;
      const isQuarter = !isWhole && !isHalf && i % 4 === 0;
      const isEighth = !isWhole && !isHalf && !isQuarter && i % 2 === 0;
      const kind: InchKind = isWhole
        ? "major"
        : isHalf
        ? "half"
        : isQuarter
        ? "quarter"
        : isEighth
        ? "eighth"
        : "sixteenth";
      ticks.push({
        x,
        label: isWhole ? `${sixteenth}`.replace(/\.0$/, "") : undefined,
        kind,
      });
    }
    return ticks.filter((t) => t.x <= lengthPx);
  }, [lengthPx, pxPerInch]);

  const cmTicks = useMemo(() => {
    type CmKind = "cm" | "halfCm" | "mm";
    const count = Math.floor(lengthPx / pxPerCm) + 1;
    const ticks: { x: number; label?: string; kind: CmKind }[] = [];
    for (let i = 0; i <= count * 10; i++) {
      const tenth = i / 10; // millimeters as tenths of a centimeter
      const x = Math.round(tenth * pxPerCm);
      const isWhole = i % 10 === 0; // 1 cm
      const isHalf = !isWhole && i % 5 === 0; // 5 mm
      const kind: CmKind = isWhole ? "cm" : isHalf ? "halfCm" : "mm";
      ticks.push({
        x,
        label: isWhole ? `${tenth}`.replace(/\.0$/, "") : undefined,
        kind,
      });
    }
    return ticks.filter((t) => t.x <= lengthPx);
  }, [lengthPx, pxPerCm]);

  return { inchTicks, cmTicks, pxPerInch, pxPerCm };
}

export default function Ruler({ lengthPx = 800, windowId }: RulerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: lengthPx, height: 80 });
  const rotationDeg = useWindowStore((s) =>
    windowId ? s.windows[windowId]?.rotationDeg ?? 0 : 0
  );
  const updateRotation = useWindowStore((s) => s.updateRotation);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr) return;
      setSize((s) => ({ width: Math.max(cr.width, 200), height: s.height }));
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const { inchTicks, cmTicks } = useTicks(size.width);

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full select-none text-foreground"
      style={{
        background: "transparent",
        WebkitUserSelect: "none",
        userSelect: "none",
      }}
      aria-label="Ruler"
    >
      {windowId && (
        <RotateHandle
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-card/70 border border-border shadow no-drag"
          title="Rotate"
          initialDeg={rotationDeg}
          getCenter={() => {
            const root = containerRef.current;
            if (!root) return null;
            const rect = root.getBoundingClientRect();
            return {
              x: rect.left + rect.width / 2,
              y: rect.top + rect.height / 2,
            };
          }}
          onChange={(deg) => updateRotation(windowId, deg)}
        />
      )}
      <svg
        width={size.width}
        height={80}
        role="img"
        aria-label="Measurement ruler with inches and centimeters"
        style={{ display: "block" }}
      >
        {(() => {
          // ratio-based measures
          const baseH = 80;
          const inMajor = Math.max(16, baseH * 0.24);
          const inHalf = Math.max(13, inMajor * 0.8);
          const inQuarter = Math.max(11, inMajor * 0.68);
          const inEighth = Math.max(9, inMajor * 0.55);
          const inSixteenth = Math.max(7, inMajor * 0.42);
          const cmMajor = inMajor;
          const cmHalf = Math.max(11, inMajor * 0.66);
          const cmMinor = Math.max(8, inMajor * 0.5);
          const swMajor = 1.2;
          const swMedium = 1.0;
          const swMinor = 0.8;
          const labelFont = 10;
          return (
            <g>
              <defs>
                <pattern
                  id="ruler-backing"
                  width={8}
                  height={8}
                  patternUnits="userSpaceOnUse"
                >
                  <rect width="8" height="8" fill="rgba(255,255,255,0.08)" />
                </pattern>
              </defs>
              <rect
                x="0"
                y="0"
                width="100%"
                height="100%"
                fill="rgba(255,255,255,0.05)"
              />
              {/* Subtle background pattern overlay */}
              <rect
                x="0"
                y="0"
                width="100%"
                height="100%"
                fill="url(#ruler-backing)"
                fillOpacity={0.18}
              />

              {/* Center baseline */}
              <line
                x1={0.5}
                y1={baseH / 2}
                x2={size.width - 0.5}
                y2={baseH / 2}
                stroke="currentColor"
                strokeOpacity={0.18}
                strokeWidth={0.75}
              />

              {/* Inches scale - top */}
              <g transform="translate(0,0)">
                {inchTicks.map((t, idx) => {
                  const h =
                    t.kind === "major"
                      ? inMajor
                      : t.kind === "half"
                      ? inHalf
                      : t.kind === "quarter"
                      ? inQuarter
                      : t.kind === "eighth"
                      ? inEighth
                      : inSixteenth;
                  const sw =
                    t.kind === "major"
                      ? swMajor
                      : t.kind === "half" || t.kind === "quarter"
                      ? swMedium
                      : swMinor;
                  return (
                    <g key={`in-${idx}`}>
                      <line
                        x1={t.x}
                        y1={0}
                        x2={t.x}
                        y2={h}
                        stroke="currentColor"
                        strokeOpacity={0.8}
                        strokeWidth={sw}
                      />
                      {t.label && t.label !== "0" && (
                        <text
                          x={t.x}
                          y={h + 10}
                          fontSize={labelFont}
                          textAnchor="middle"
                          fill="currentColor"
                          fillOpacity={0.85}
                        >
                          {t.label}
                        </text>
                      )}
                    </g>
                  );
                })}
                <text
                  x={6}
                  y={inMajor + 20}
                  fontSize={10}
                  fill="currentColor"
                  fillOpacity={0.65}
                >
                  in
                </text>
              </g>

              {/* Centimeters scale - bottom */}
              <g transform="translate(0,80)">
                {cmTicks.map((t, idx) => {
                  const h =
                    t.kind === "cm"
                      ? cmMajor
                      : t.kind === "halfCm"
                      ? cmHalf
                      : cmMinor;
                  const sw =
                    t.kind === "cm"
                      ? swMajor
                      : t.kind === "halfCm"
                      ? swMedium
                      : swMinor;
                  return (
                    <g key={`cm-${idx}`}>
                      <line
                        x1={t.x}
                        y1={0}
                        x2={t.x}
                        y2={-h}
                        stroke="currentColor"
                        strokeOpacity={0.8}
                        strokeWidth={sw}
                      />
                      {t.label && t.label !== "0" && (
                        <text
                          x={t.x}
                          y={-h - 6}
                          fontSize={labelFont}
                          textAnchor="middle"
                          fill="currentColor"
                          fillOpacity={0.85}
                        >
                          {t.label}
                        </text>
                      )}
                    </g>
                  );
                })}
                <text
                  x={6}
                  y={-cmHalf - 12}
                  fontSize={10}
                  fill="currentColor"
                  fillOpacity={0.65}
                >
                  cm
                </text>
              </g>

              {/* Outline border so user can match edges to content */}
              <rect
                x={0.5}
                y={0.5}
                width={size.width - 1}
                height={79}
                rx={6}
                ry={6}
                fill="none"
                stroke="currentColor"
                strokeOpacity={0.6}
              />
            </g>
          );
        })()}
      </svg>
    </div>
  );
}
